import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, CircleCheck, Info } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { useAjudaPapel } from "@/core/hooks/useAjudaPapel";
import { useProgressoAjuda } from "@/core/hooks/useProgressoAjuda";
import { montarLicoes, type LicaoMontada } from "@/core/ajuda/licoes";
import { contarVistas } from "@/core/ajuda/progresso";
import { BTN_TEXTO, PAGINA_AJUDA } from "@/core/components/ajuda/ajudaUi";

/** Lições em blocos: as sem grupo primeiro e, depois, cada grupo (ex.: "Seus elos") com o seu título. */
function emBlocos(licoes: LicaoMontada[]) {
  const blocos: { grupo: string | null; itens: { licao: LicaoMontada; numero: number }[] }[] = [];
  licoes.forEach((licao, i) => {
    const ultimo = blocos[blocos.length - 1];
    if (ultimo && ultimo.grupo === licao.grupo) ultimo.itens.push({ licao, numero: i + 1 });
    else blocos.push({ grupo: licao.grupo, itens: [{ licao, numero: i + 1 }] });
  });
  return blocos;
}

/**
 * Lista de lições da Ajuda do Elos: é o que o menu da conta abre. Qualquer
 * lição pode ser aberta direto, em qualquer ordem, sem refazer as outras.
 * Quem lidera ou gerencia um elo vê também as lições do seu papel e, no grupo
 * "Seus elos", uma lição para cada elo que pode cuidar.
 */
export function AjudaLista() {
  const navigate = useNavigate();
  const { modulosVisiveis } = useAuth();
  const { papel } = useAjudaPapel();
  const { progresso } = useProgressoAjuda();
  const licoes = useMemo(() => montarLicoes(modulosVisiveis, papel), [modulosVisiveis, papel]);
  const blocos = useMemo(() => emBlocos(licoes), [licoes]);
  const vistas = contarVistas(
    progresso,
    licoes.map((l) => l.id),
  );
  const tituloRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    tituloRef.current?.focus();
  }, []);

  return (
    <main className={PAGINA_AJUDA}>
      <header className="mb-5">
        <h1
          ref={tituloRef}
          tabIndex={-1}
          className="text-2xl font-black tracking-tight text-primary outline-none sm:text-3xl"
        >
          Ajuda do Elos
        </h1>
        <p className="mt-1 text-base text-muted">
          {vistas} de {licoes.length} lições vistas. Escolha uma, em qualquer ordem.
        </p>
      </header>

      <div className="flex flex-col gap-6">
        {blocos.map((bloco) => (
          <section key={bloco.grupo ?? "geral"} aria-label={bloco.grupo ?? "Lições para todos"}>
            {bloco.grupo && (
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted">
                {bloco.grupo}
              </h2>
            )}
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {bloco.itens.map(({ licao: l, numero }) => {
                const vista = progresso.vistas.includes(l.id);
                return (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/ajuda/${l.id}`)}
                      className="group flex min-h-16 w-full items-center gap-4 rounded-2xl border border-border bg-surface p-4 text-left transition-colors hover:border-primary"
                    >
                      <span
                        aria-hidden="true"
                        className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-base font-black ${
                          vista
                            ? "bg-success/15 text-success"
                            : "bg-primary/10 text-primary dark:text-text"
                        }`}
                      >
                        {vista ? <Check size={20} /> : numero}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-lg font-bold text-text">{l.titulo}</span>
                        <span className="block text-base text-muted">{l.resumo}</span>
                        {vista && (
                          <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-success">
                            <CircleCheck size={14} aria-hidden="true" />
                            Vista
                          </span>
                        )}
                        {l.nota && (
                          <span className="mt-1 flex items-center gap-1 text-sm font-semibold text-muted">
                            <Info size={14} aria-hidden="true" className="shrink-0" />
                            {l.nota}
                          </span>
                        )}
                      </span>
                      <ChevronRight
                        size={20}
                        aria-hidden="true"
                        className="shrink-0 text-muted transition-transform group-hover:translate-x-0.5"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-between">
        <button type="button" onClick={() => navigate("/ajuda/apresentacao")} className={BTN_TEXTO}>
          Rever a apresentação
        </button>
        <button type="button" onClick={() => navigate("/inicio")} className={BTN_TEXTO}>
          Sair da Ajuda
        </button>
      </div>
    </main>
  );
}
